import React from 'react';
import { Box, Typography, Tooltip, IconButton } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import StyleOutlinedIcon from '@mui/icons-material/StyleOutlined';

/* ─── Paleta ──────────────────────────────────────────────────── */
export const PRIMARY      = '#f25d27';
export const PRIMARY_DARK = '#d44e1f';
export const WARNING      = '#ed6c02';

/* ─── Helpers ─────────────────────────────────────────────────── */
export const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

/* ─── Anexos ──────────────────────────────────────────────────── */
export const ALLOWED_ATTACHMENT_EXTENSIONS = ['mp3', 'wav', 'flac', 'txt', 'doc', 'docx', 'csv', 'xls', 'xlsx', 'pdf', 'ppt'];
export const ATTACHMENT_ACCEPT = ALLOWED_ATTACHMENT_EXTENSIONS.map((ext) => `.${ext}`).join(',');
export const ALLOWED_ATTACHMENT_LABEL = 'MP3, WAV, FLAC, TXT, DOC, DOCX, CSV, XLS, XLSX, PDF ou PPT';

export const getFileExtension = (filename = '') => {
    const parts = filename.split('.');
    return parts.length > 1 ? parts.pop().toLowerCase() : '';
};

export const dedupeFiles = (existing, incoming) => {
    const keys = new Set(existing.map((f) => `${f.name}-${f.size}-${f.lastModified}`));
    const merged = [...existing];
    incoming.forEach((file) => {
        const key = `${file.name}-${file.size}-${file.lastModified}`;
        if (!keys.has(key)) {
            keys.add(key);
            merged.push(file);
        }
    });
    return merged;
};

/** Chip de anexo — usado tanto na seleção pendente quanto no histórico */
export const AttachmentChip = ({ attachment, onRemove, dark }) => {
    const name = attachment?.name || attachment?.filename || 'arquivo';
    const url = attachment?.url;
    const isLocalFile = typeof File !== 'undefined' && attachment instanceof File;

    const handleDownload = (e) => {
        e.stopPropagation();
        if (url) {
            window.open(url, '_blank', 'noopener,noreferrer');
            return;
        }
        if (isLocalFile) {
            const blobUrl = URL.createObjectURL(attachment);
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = name;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(blobUrl);
        }
    };

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1,
            py: 0.4,
            borderRadius: '8px',
            backgroundColor: dark ? 'rgba(255,255,255,0.18)' : '#f0f0f0',
            border: dark ? 'none' : '1px solid #e5e5e5',
            maxWidth: '100%',
        }}>
            <AttachFileIcon sx={{ fontSize: 13, color: dark ? '#fff' : '#777', flexShrink: 0 }} />
            <Typography sx={{
                fontSize: '0.72rem',
                color: dark ? '#fff' : '#555',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
                minWidth: 0,
            }}>
                {name}
            </Typography>
            <Tooltip title="Baixar anexo">
                <IconButton size="small" onClick={handleDownload} sx={{ p: 0.2, color: dark ? '#fff' : '#888' }}>
                    <DownloadIcon sx={{ fontSize: 13 }} />
                </IconButton>
            </Tooltip>
            {onRemove && (
                <Tooltip title="Remover anexo">
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); onRemove(); }} sx={{ p: 0.2, color: dark ? '#fff' : '#d32f2f' }}>
                        <CloseIcon sx={{ fontSize: 13 }} />
                    </IconButton>
                </Tooltip>
            )}
        </Box>
    );
};

/** Chip da área editorial selecionada — mesmo padrão visual do anexo, com opção de remover */
export const EditorialAreaChip = ({ name, onRemove }) => (
    <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        py: 0.4,
        borderRadius: '8px',
        backgroundColor: '#f0f0f0',
        border: '1px solid #e5e5e5',
        maxWidth: '100%',
    }}>
        <StyleOutlinedIcon sx={{ fontSize: 13, color: '#777', flexShrink: 0 }} />
        <Typography sx={{
            fontSize: '0.72rem',
            color: '#555',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
            minWidth: 0,
        }}>
            {name}
        </Typography>
        {onRemove && (
            <Tooltip title="Remover área editorial">
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); onRemove(); }} sx={{ p: 0.2, color: '#d32f2f' }}>
                    <CloseIcon sx={{ fontSize: 13 }} />
                </IconButton>
            </Tooltip>
        )}
    </Box>
);

/** Balão de instrução do usuário — texto e anexos no mesmo card. Não renderiza nada se não houver texto nem anexos. */
export const InstructionBubble = ({ text, attachments }) => {
    const hasAttachments = attachments && attachments.length > 0;
    if (!text && !hasAttachments) return null;

    return (
        <Box sx={{
            alignSelf: 'flex-end',
            maxWidth: '85%',
            backgroundColor: PRIMARY,
            color: '#fff',
            borderRadius: '16px 16px 4px 16px',
            px: 2,
            py: 1.2,
            fontSize: '0.88rem',
            lineHeight: 1.6,
            wordBreak: 'break-word',
        }}>
            {text && <Box>{text}</Box>}
            {hasAttachments && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, mt: text ? 1 : 0 }}>
                    {attachments.map((att, i) => (
                        <AttachmentChip key={att.id ?? `${att.name}-${i}`} attachment={att} dark />
                    ))}
                </Box>
            )}
        </Box>
    );
};
